import client from "../../../index";
import { GET_CONTACTS, GET_CONTACTS_FULL, GET_CONTACT } from "./query";
import { CREATE_CONTACT, UPDATE_CONTACT, DELETE_CONTACT, CHAT_ABOUT_CONTACT, SEND_EMAIL_TO_CATEGORY } from "./mutation";

const NO_CACHE = "no-cache";

const compactObject = (value) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

const stripTypenames = (value) => {
  if (value == null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(stripTypenames);
  const { __typename, ...rest } = value;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypenames(v)]));
};

const normalizeContact = (contact) => {
  if (!contact || typeof contact !== "object") return contact;
  // GraphQL Contact type exposes `_id`, not `id`. Map it so card selection
  // (selectedId === card.id) can distinguish between contacts.
  return { ...contact, id: String(contact._id || contact.id || "").trim() };
};

export const buildContactsInput = ({
  filters = {},
  pagination = { page: 1, limit: 25 },
  ordering = { field: "updatedAt", order: "Desc" },
} = {}) => {
  return compactObject({
    ...compactObject(filters),
    pagination: pagination ? compactObject(pagination) : undefined,
    ordering: ordering ? compactObject(ordering) : undefined,
  });
};

// Pass `full: true` for flows that need the complete document (incl. the
// `data` bag — e.g. bulk-email eligibility, verify workflows, or any caller
// that later WRITES contact.data). Default is the slim card fragment.
export const apiGetContactsCollection = async ({ full = false, ...params } = {}) => {
  const input = buildContactsInput(params);
  const res = await client.query({
    query: full ? GET_CONTACTS_FULL : GET_CONTACTS,
    variables: { input },
    fetchPolicy: NO_CACHE,
  });

  return {
    total: res?.data?.contacts?.total || 0,
    items: (res?.data?.contacts?.contacts || []).map(normalizeContact),
  };
};

export const apiGetContactById = async ({ id, authorId }) => {
  const res = await client.query({
    query: GET_CONTACT,
    variables: { id, authorId },
    fetchPolicy: NO_CACHE,
  });
  return normalizeContact(res?.data?.contact || null);
};

export const apiCreateContact = async (input) => {
  // Backend derives authorId from the JWT context. The GraphQL
  // CreateContactInput schema does not accept authorId, so passing it
  // through trips a "Field not allowed" validation error and returns
  // HTTP 400 before the resolver runs. Strip it here.
  const { authorId, _id, id, ...safeInput } = input || {};
  void authorId; void _id; void id;
  const res = await client.mutate({
    mutation: CREATE_CONTACT,
    variables: { input: stripTypenames(safeInput) },
    fetchPolicy: NO_CACHE,
  });
  return normalizeContact(res?.data?.createContact?.contact || null);
};

export const apiUpdateContact = async (input) => {
  const { authorId, domain, _id, ...safeInput } = input || {};
  const normalizedId = safeInput.id || _id;
  const res = await client.mutate({
    mutation: UPDATE_CONTACT,
    variables: { input: stripTypenames({ ...safeInput, id: normalizedId }) },
    fetchPolicy: NO_CACHE,
  });
  return normalizeContact(res?.data?.updateContact?.contact || null);
};

export const apiDeleteContact = async ({ id, authorId }) => {
  const res = await client.mutate({
    mutation: DELETE_CONTACT,
    variables: { id, authorId },
    fetchPolicy: NO_CACHE,
  });
  return res?.data?.deleteContact?.id || null;
};

export const apiChatAboutContact = async ({ id, authorId, message, history }) => {
  const res = await client.mutate({
    mutation: CHAT_ABOUT_CONTACT,
    variables: { input: { id, authorId, message, history } },
    fetchPolicy: NO_CACHE,
  });
  return res?.data?.chatAboutContact?.response || "";
};

export const apiSendEmailToCategory = async ({
  authorId,
  domain,
  category,
  subject,
  body,
  from,
  replyTo,
  dryRun,
  compliance,
  retryPolicy,
  campaignId,
  runId,
  recipientIds,
}) => {
  const input = compactObject({
    authorId,
    domain,
    category,
    subject,
    body,
    from,
    replyTo,
    dryRun,
    compliance,
    retryPolicy,
    campaignId,
    runId,
    recipientIds,
  });
  const res = await client.mutate({
    mutation: SEND_EMAIL_TO_CATEGORY,
    variables: { input },
    fetchPolicy: NO_CACHE,
  });
  return res?.data?.sendEmailToCategory || null;
};
